import { fmtDate } from '../lib/format';
import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays, Plus, Pencil, X } from 'lucide-react';
import { useFetch, Spinner } from '../lib/useFetch';
import { calendarApi, companiesApi } from '../lib/api';
import { useCrudActions } from '../lib/useCrudActions';
import EntityFormModal from '../components/EntityFormModal';
import { calendarFields } from '../lib/entitySchemas';
import { useAuth } from '../context/AuthContext';

const typeColors = {
  formazione: { bg: '#fee2e2', color: '#b91c1c', label: 'Formazione' },
  visita: { bg: '#e0e7ff', color: '#023F80', label: 'Visita medica' },
  dpi: { bg: '#fef7d4', color: '#8a6608', label: 'DPI' },
  haccp: { bg: '#dcfce7', color: '#1e40af', label: 'HACCP' },
  sicurezza: { bg: '#e0e7ff', color: '#023F80', label: 'Sicurezza' },
};

const CalendarioPage = () => {
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const today = new Date();
  const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [filter, setFilter] = useState('all');

  const { data: allEvents, loading, reload } = useFetch(() => calendarApi.list(), []);
  const { data: companies } = useFetch(() => companiesApi.list(), []);
  const crud = useCrudActions({ api: calendarApi, label: 'evento', onChange: reload });

  const events = useMemo(() => {
    let list = allEvents || [];
    if (filter !== 'all') list = list.filter((e) => e.type === filter);
    return list;
  }, [filter, allEvents]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const startWeekday = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  const dayNames = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const eventsByDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter((e) => e.date === dateStr);
  };

  const isToday = (day) => day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  const upcomingEvents = (events || [])
    .filter((e) => new Date(e.date) >= today)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 8);

  if (loading) return <Spinner />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#e0e7ff] text-[#023F80] flex items-center justify-center"><CalendarDays size={20} /></div>
            <h2 className="text-lg font-bold text-gray-800">Calendario scadenze</h2>
          </div>
          <button onClick={() => crud.onCreate({ type: 'scadenza', color: '#023F80', date: new Date().toISOString().slice(0, 10) })} className="ro-hide px-4 py-2 rounded-full bg-[#fef7d4] text-[#8a6608] text-xs font-bold tracking-wider hover:bg-[#E6B800] flex items-center gap-2"><Plus size={14} /> NUOVO EVENTO</button>
        </div>

        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setViewDate(new Date(year, month - 1, 1))} className="p-2 rounded-md hover:bg-gray-100"><ChevronLeft size={18} /></button>
          <h3 className="text-base font-bold text-gray-800">{monthNames[month]} {year}</h3>
          <button onClick={() => setViewDate(new Date(year, month + 1, 1))} className="p-2 rounded-md hover:bg-gray-100"><ChevronRight size={18} /></button>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={() => setFilter('all')} className={`px-3 py-1 rounded-full text-xs font-semibold ${filter === 'all' ? 'bg-[#023F80] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Tutti</button>
          {Object.entries(typeColors).map(([k, v]) => (
            <button key={k} onClick={() => setFilter(k)} className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: filter === k ? v.color : v.bg, color: filter === k ? '#fff' : v.color }}>{v.label}</button>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold text-gray-500 uppercase mb-1">
          {dayNames.map((d) => <div key={d} className="py-2">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((day, idx) => {
            if (!day) return <div key={idx} className="h-24 rounded-lg bg-gray-50/40" />;
            const dayEvents = eventsByDay(day);
            return (
              <div key={idx} className={`h-24 rounded-lg border p-1.5 flex flex-col text-left ${isToday(day) ? 'border-[#023F80] bg-[#e0e7ff]/40' : 'border-gray-100 hover:bg-gray-50/60'}`}>
                <div className={`text-xs font-bold ${isToday(day) ? 'text-[#023F80]' : 'text-gray-700'}`}>{day}</div>
                <div className="flex-1 mt-1 space-y-0.5 overflow-hidden">
                  {dayEvents.slice(0, 2).map((e) => {
                    const t = typeColors[e.type];
                    return <div key={e.id} title={e.title} className="truncate text-[9px] px-1 py-0.5 rounded font-semibold" style={{ background: t.bg, color: t.color }}>{e.title.split('-')[0]}</div>;
                  })}
                  {dayEvents.length > 2 && <div className="text-[9px] text-gray-500">+{dayEvents.length - 2}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <h3 className="font-bold text-gray-800 mb-3">Prossime scadenze</h3>
        <div className="space-y-2">
          {upcomingEvents.length === 0 && <div className="text-sm text-gray-400 py-6 text-center">Nessuna scadenza in arrivo</div>}
          {upcomingEvents.map((e) => {
            const t = typeColors[e.type];
            const c = companies?.find((cc) => cc.id === e.companyId);
            return (
              <div key={e.id} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors group">
                <div className="w-10 text-center shrink-0">
                  <div className="text-[10px] font-bold uppercase text-gray-500">{monthNames[new Date(e.date).getMonth()].slice(0, 3)}</div>
                  <div className="text-lg font-extrabold text-gray-800">{new Date(e.date).getDate()}</div>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-800 leading-tight">{e.title}</div>
                  <div className="text-[11px] text-gray-500 mt-1">{c?.name}</div>
                  <span className="inline-block mt-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: t?.bg, color: t?.color }}>{t?.label || e.type}</span>
                </div>
                <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => crud.onEdit(e)} title="Modifica" className="ro-hide p-1 rounded hover:bg-white"><Pencil size={12} className="text-gray-600" /></button>
                  <button onClick={() => crud.onDelete(e)} title="Elimina" className="ro-hide p-1 rounded hover:bg-white"><X size={13} className="text-red-600" /></button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <EntityFormModal
        open={crud.open}
        onOpenChange={(o) => { if (!o) crud.closeModal(); }}
        title={crud.editing?.id ? 'Modifica evento' : 'Nuovo evento calendario'}
        fields={calendarFields(companies || [], isClient)}
        initialValues={crud.editing || {}}
        onSubmit={crud.save}
      />
    </div>
  );
};

export default CalendarioPage;
